import React from 'react';
import Link from 'next/link';

import isProtectedRoute from '../../src/utils/isProtectedRoute';

// Extract the hash (if any) from a react-router `to` prop, which can be either a
// string ("/path#section") or a location-like object ({ pathname, hash }).
const hasHashTarget = to => {
  if (typeof to === 'string') return to.includes('#');
  if (to && typeof to === 'object') return Boolean(to.hash);
  return false;
};

// Pathname of a react-router `to` prop, without query string or hash.
const getPathname = to => {
  if (typeof to === 'string') return to.split(/[?#]/)[0];
  if (to && typeof to === 'object' && typeof to.pathname === 'string') {
    return to.pathname;
  }
  return '';
};

const LinkMock = React.forwardRef((props, ref) => {
  const { to, scroll, prefetch, ...rest } = props;

  // CON-8199: protected routes (e.g. /customer/orders/, /wishlist/, /quote/)
  // are redirected to /sign-in/ by the proxy while logged out. Prefetching one
  // makes the App Router cache that redirect resolution, and the cached entry
  // is replayed when the app navigates to the same route after login - which
  // strands the user back on /sign-in/ instead of the page they asked for.
  // Never prefetch them; the cache can then never be poisoned, whichever
  // component rendered the link.
  //
  // This is deliberately not overridable: a caller-supplied `prefetch` is
  // ignored for protected routes, because a Link cannot know whether the user
  // is signed in and so can never establish that prefetching one is safe.
  // Public routes keep full control over the prop.
  const resolvedPrefetch = isProtectedRoute(getPathname(to)) ? false : prefetch;

  // Next.js manages scroll on navigation via its ScrollAndFocusHandler. During
  // PPR/streaming it resolves the scroll target from a layout-router segment as
  // each segment mounts, which can land the viewport partway down the page and
  // override our ScrollToTop reset (CON-8326). Disable Next's scroll handling
  // for ordinary page navigations and let ScrollToTop own the scroll-to-top.
  //
  // Hash links (in-page anchors) still need Next's scroll-to-element behaviour,
  // so we leave scroll at its default for those. Callers can always pass an
  // explicit `scroll` prop to override.
  const resolvedScroll = scroll ?? (hasHashTarget(to) ? undefined : false);

  return (
    <Link
      {...rest}
      scroll={resolvedScroll}
      prefetch={resolvedPrefetch}
      href={to}
      ref={ref}
    />
  );
});

export default LinkMock;
